/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';

import {EMPTY, forkJoin, merge, Observable, of, Subject} from 'rxjs';
import {catchError, expand, filter, finalize, map, switchMap, take, takeUntil, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {PayExecAttributes, PayExecType, PayExecWithBeginDate, RetroValue} from './pay-results.model';
import {PayResultsService} from './pay-results.service';

import {AccordionPanelComponent} from 'ngx-bootstrap/accordion';
import {TabsetComponent} from 'ngx-bootstrap/tabs';
import {RESIZE_EVENT} from '../../app.constants';
import {Destroyable} from '../../destroyable.component';
import {EmpContractAttributes} from '../../models/models.interfaces';
import {Data} from '../../shared/api';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {Contract, Employee, PayPeriodContract} from '../../shared/employees/employees.model';
import {EmployeesService} from '../../shared/employees/employees.service';
import {EmployeesContextUtilsService} from '../../shared/employees/utils/employee-context-utils.service';
import {PayExecUtils} from '../../shared/employees/utils/pay-exec-utils.service';
import {
  ComponentView,
  DateRange,
  NodeSelection,
  TimelineConfig,
  TimelineData,
  TimelineDataSegmentHandler,
  TimelineDataSet
} from '../../shared/ui/horizontal-timeline/horizontal-timeline.model';
import {User, UserService} from '../../shared/user';
import {OngoingPayrollsService} from '../ongoing/ongoing-payrolls.service';
import {PayTypeContextChange, PayTypeWithCliRegIds} from '../payrolls.model';
import {EmployeeVariablesService} from '../prepare/variable-elements/employee-variables.service';
import {PayrollUtilsService} from '../utils/payroll-utils.service';

export type PayExecLegacy = Data<PayExecAttributes>;
// In-memory storage of the initial tab to open the result on
let initialTab: string = 'Overview';

/**
 * Employee detail : displays the employee form for a contract of the employee, letting the employee select the contract
 * to edit.
 */
@Component({
  selector: 'gp-pay-result-detail',
  templateUrl: './pay-result-detail.component.html',
  styleUrls: ['./pay-result-detail.component.scss']
})
export class PayResultDetailComponent extends Destroyable implements OnInit, AfterViewInit {

  @ViewChild('payResultTabset') payResultTabset: TabsetComponent;

  public PayExecType = PayExecType;

  public employee: Employee;
  public contractId: number;
  public contracts: Contract[] = [];
  public selectedContract: Contract;

  public user: User;

  public selectedPayExec: PayExecLegacy;

  public highlight: string;

  public showPeriods: boolean = true;
  public payExecLog: any;

  public readyTabs: { [key: string]: boolean } = {};

  public payslipHeight: string = '200px';
  public payslipPdfFileId: number;
  public payslipHtmlFileId: number;
  public payPeriodId: number;

  public loading: boolean = false;

  public isReadOnly: boolean = true;
  public isAdministrator: boolean = false;
  public isFeatureToggleForPayrollDashboardEnabled = false;
  public isPayrollStatusLoading: boolean = false;
  public selectedPayExecId: number;

  public lastGoodExecutionIds: number[] = [];

  public payPeriodContracts: PayPeriodContract[] = [];
  public selectedPayType: PayTypeContextChange;
  public payTypes: PayTypeWithCliRegIds[];

  private routeParams: any;
  private payrollStatusChanged$: Subject<any> = new Subject();

  public retroValue: RetroValue[] = [];
  public retroPayslipsCount: number = 0;

  public timelineConfig: TimelineConfig;
  public timelineDataSet: TimelineDataSet;
  public timelineData: TimelineData;
  public timelineDataHandler: Subject<TimelineDataSegmentHandler>;
  public timelineLandingDate: Date;
  public timelineLoading: boolean;
  public timelineInitialised: boolean;
  public timelineInfoLoading: boolean;
  public payExecsDated: PayExecWithBeginDate[];
  public payExecNodeSelection: NodeSelection;
  public payExecsByPayType: Map<string, PayExecWithBeginDate[]>;
  public displayablePayTypeTransId: Map<string, string>;
  public payExecUtilsPublic: PayExecUtils;
  public shouldCloseOpenedViewCard: boolean;

  private payExecPagesFetchCount = 5;
  private payExecPagesFetched: number;
  private totalPayExecPagesToFetch: number;
  private payExecsDateRangeToFetch: DateRange[] = [];
  private periods: any[];
  private dateRange: DateRange;
  private highlightedPayExec: PayExecWithBeginDate;
  @ViewChildren('payTypeGroup') private payTypeGroupElements !: QueryList<AccordionPanelComponent>;

  constructor(private employeesService: EmployeesService,
              private employeeVariablesService: EmployeeVariablesService,
              private payResultsService: PayResultsService,
              private userService: UserService,
              private cd: ChangeDetectorRef,
              private route: ActivatedRoute,
              private router: Router,
              private rolesService: RolesService,
              private employeesContextService: EmployeesContextUtilsService,
              private ongoingPayrollService: OngoingPayrollsService,
              private payrollUtilsService: PayrollUtilsService,
              private payExecUtils: PayExecUtils) {
    super();
    this.payExecUtilsPublic = payExecUtils;
  }

  public ngOnInit() {
    this.selectedPayType = this.employeesContextService.getContextPayType();

    this.ongoingPayrollService.getLastExecutionId
      .pipe(
        take(1)
      ).subscribe(id => {
      this.selectedPayExecId = id;
      this.ongoingPayrollService.updateLastExecutionId(null);
    });

    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe((user: User) => this.user = user);

    merge(this.route.params, this.payrollStatusChanged$)
      .pipe(
        filter((params: any) => {
          this.routeParams = params;

          this.highlight = params.line;
          const contractId = parseInt(params.contractId, 10) || undefined;
          if (params.tab) {
            initialTab = params.tab;
          }

          if (this.contractId === contractId) {
            return false;
          } else if (this.contractId) {
            this.selectedPayExecId = null;
          }

          this.contractId = contractId;

          return true;
        }),
        tap(() => this.loading = true),
        switchMap(() => forkJoin([
          this.getEmployeeData(),
          this.getOngoingPayPeriod(),
          this.initPayExecTimelineData()
        ])),
        switchMap(() => this.fetchPayTypes$()),
        tap(() => this.selectContract()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => this.loading = false);

    this.listenContextPayType();

    this.isReadOnly = !this.rolesService.hasRole('PAYROLL_WRITE');
    this.isAdministrator = this.rolesService.hasRole('PAYROLL_ADMINISTRATOR');
    this.isFeatureToggleForPayrollDashboardEnabled = this.rolesService.hasRole('FEATURE_TOGGLE_PAYROLL_DASHBOARD');
  }

  public ngAfterViewInit() {
    this.payTypeGroupElements.changes.subscribe((changes) => {
      setTimeout(() => {
        if (changes.length === 1) {
          if (!changes.first.isOpen) {
            changes.first.toggleOpen();
          }
        } else {
          this.payTypeGroupElements.filter(el => el.isOpen).forEach(el => el.toggleOpen());
        }
      });
    });
  }

  public payrollStatusChanged(payroll: any) {
    this.isPayrollStatusLoading = false;
    this.contractId = null;
    this.payrollStatusChanged$.next(this.routeParams);
  }

  public onPayExecTimelineInitialised(event: DateRange) {
    this.payExecsDateRangeToFetch.push(event);
    this.loadMorePayExecs(true).subscribe();
  }

  public onLoadPreviousPayExecs(event: DateRange) {
    this.payExecsDateRangeToFetch.push(event);
    this.loadMorePayExecs(false).subscribe();
  }

  public onPayExecNodeSelection(event: NodeSelection) {
    const payExecs: PayExecWithBeginDate[] = this.getPayExecsFromNodeSelectionData(event);
    if (!this.filterPayExecList(payExecs).length) {
      event.open = false;
      return;
    }

    this.payExecNodeSelection = event;
    this.payExecsByPayType = this.groupPayExecsByPayType(payExecs);
    this.selectDefaultPayExec();
  }

  public getDisplayablePayTypeKeys(): string[] {
    if (!this.displayablePayTypeTransId) {
      return [];
    }
    return [...this.displayablePayTypeTransId.keys()];
  }

  public selectDefaultPayExec() {
    const nonRetroPayExec = (this.payExecsByPayType.get('default'))
      .filter(pay => !(pay.payExec.attributes.payRollType === PayExecType.RETROACTIVITY));
    this.highlightPayExec(nonRetroPayExec[0]);
    this.selectPayExec(this.highlightedPayExec.payExec);
  }

  public getCurrentPayExecBeginDate() {
    return this.highlightedPayExec?.payPeriodBeginDate || this.timelineLandingDate;
  }

  public getPayExecsFromNodeSelectionData(event: NodeSelection) {
    return event?.data.get(this.timelineData).map(r => <PayExecWithBeginDate>r);
  }

  public highlightPayExec(payExec: PayExecWithBeginDate) {
    this.highlightedPayExec = payExec;
  }

  public confirmPayExecSelection() {
    this.closePayExecSelection();
    this.selectPayExec(this.highlightedPayExec.payExec);
  }

  public closePayExecSelection() {
    this.payExecNodeSelection.open = false;
    this.shouldCloseOpenedViewCard = false;
  }

  public isHighlightedPayExec(payExec: PayExecWithBeginDate): boolean {
    return payExec.payExec.id === this.highlightedPayExec?.payExec.id;
  }

  public retroPayExecCount(payExecs: PayExecWithBeginDate[]): number {
    return (payExecs || []).filter(payExec => payExec.payExec.attributes.payRollType === PayExecType.RETROACTIVITY).length;
  }

  public isRetroSourcePayExec(payExecs: PayExecWithBeginDate[], node: HTMLElement): void {
    const closest = node.closest('.timeline-node');
    if (this.findExecs(payExecs, true)) {
      closest.classList.add('badge-retro-source');
    } else {
      closest.classList.remove('badge-retro-source');
    }
  }

  public selectionHasRetroPayExec(payExecs: PayExecWithBeginDate[]): boolean {
    return this.findExecs(payExecs, false);
  }

  public scrollPayExecs(event: WheelEvent, element: HTMLElement) {
    event.preventDefault();
    element.scrollTop += event.deltaY;
  }

  private selectPayExec(payExec: PayExecLegacy) {
    this.selectedPayExec = payExec;
    this.setPayPeriodId(this.selectedPayExec.attributes.payPeriodContract);
    this.readyTabs = {};
    this.readyTabs[initialTab] = true;
    this.payExecLog = null;

    this.setPayslipIds();
    this.getRetroValue();
    this.countRetroPayslips();

    return this.payResultsService.getPayExecLog(this.selectedPayExec.id)
      .subscribe((data: any) => {
        this.payExecLog = data;
        if ((this.isTabActive('RetroValues') && (!this.retroValue || !this.retroValue.length)) ||
          (this.isTabActive('RetroPayslips') && !this.hasRetroPayslips())) {
          this.selectTabByIndex(0);
        }
      });
  }

  private findExecs(payExecs: PayExecWithBeginDate[], source: boolean): boolean {
    if (!this.highlightedPayExec) {
      return false;
    }
    if (this.highlightedPayExec.payExec.attributes.hasRetroPayExecs) {
      return (payExecs || []).some(payExec =>
        this.getPayExecIdBySource(payExec, source) === this.highlightedPayExec.payExec.id);
    }
    if (this.highlightedPayExec.payExec.attributes.originePayExecId) {
      return (payExecs || []).some(payExec =>
        this.getPayExecIdBySource(payExec, source) === this.highlightedPayExec.payExec.attributes.originePayExecId);
    }
    return false;
  }

  private getPayExecIdBySource = (payExec: PayExecWithBeginDate, source: boolean) => {
    return source ? payExec.payExec.id : payExec.payExec.attributes.originePayExecId;
  }

  private fetchPayTypes$(): Observable<PayTypeWithCliRegIds[]> {
    return this.payrollUtilsService.payTypesSorted$
      .pipe(
        filter(response => response !== null),
        tap((response: PayTypeWithCliRegIds[]) => {
          this.payTypes = response;
        }),
        takeUntil(this.componentDestroyed$)
      );
  }

  private groupPayExecsByPayType(payExecs: PayExecWithBeginDate[]): Map<string, PayExecWithBeginDate[]> {
    const payExecsByPayType: Map<string, PayExecWithBeginDate[]> = new Map();
    this.displayablePayTypeTransId = new Map();

    payExecs.forEach((payExec: PayExecWithBeginDate) => {
      const payTypeMatch = this.payTypes.find(payType => payType.name === payExec.payExec.attributes.payTypeName
        && payType.transId === payExec.payExec.attributes.payPeriodContractPayTypeTransId);
      const key = payTypeMatch.name;
      this.displayablePayTypeTransId.set(key, payTypeMatch.transId);
      const execs: PayExecWithBeginDate[] = payExecsByPayType.get(key) || [];
      execs.push(payExec);
      payExecsByPayType.set(key, execs);
    });
    return payExecsByPayType;
  }

  public isInternationalPayslipAvailable() {
    return this.selectedPayExec && this.selectedPayExec.attributes && this.selectedPayExec.attributes.internationalPayslipAvailable;
  }

  public getLastPayExecWithOverview() {
    return this.payExecsDated.find(payExec => payExec.payExec.attributes.internationalPayslipAvailable === true);
  }

  // TODO replace me with proper navigation
  public changeSelectedPayExec(selectionEvent) {
    this.selectedPayExecId = selectionEvent.payExecId;
    if (selectionEvent.tab) {
      this.selectTabByName(selectionEvent.tab, selectionEvent.source);
    }
  }

  private getEmployeeData(): Observable<any> {
    if (this.contractId) {
      this.selectedContract = null;
      if (this.employee && this.employee.contracts.find(c => c.id === this.contractId)) {
        return of(true);
      } else {
        this.employee = null;
        this.contracts = [];
        this.readyTabs = {};
        this.selectedPayExec = null;

        return this.employeesService.getCliEmp(this.contractId)
          .pipe(
            catchError(() => {
              return EMPTY;
            }),
            map((response: any) => {
              this.employee = new Employee(response.data);
              this.contracts = this.employee.contracts;
              return this.contractId;
            })
          );
      }
    } else {
      return EMPTY;
    }
  }

  private getPayPeriodsForContract(): Observable<any> {
    return this.employeeVariablesService.getContractPayPeriodRange(this.contractId, this.payPeriodId)
      .pipe(
        map((payPeriods: any) => payPeriods.data[0]),
        switchMap(period => this.employeeVariablesService.periodsForSeries(period.attributes.serie.id)
          .pipe(
            tap((payPeriods: any) => this.periods = payPeriods.data)
          ))
      );
  }

  private getPayPeriodContracts(): Observable<void> {
    return this.employeesService.getPayPeriodContractsByContractId([this.contractId])
      .pipe(
        catchError(() => EMPTY),
        map((response: PayPeriodContract[]) => {
          this.payPeriodContracts = response;
        })
      );
  }

  private initPayExecTimelineConfig() {
    this.timelineInitialised = false;
    this.timelineLandingDate = null;
    this.payExecPagesFetched = 0;
    this.totalPayExecPagesToFetch = 0;
    this.periods = [];
    this.highlightedPayExec = null;
    this.payExecsByPayType = new Map();
    this.payExecsDated = [];
    this.payExecNodeSelection = null;

    this.timelineConfig = new TimelineConfig();
    this.timelineConfig.view = ComponentView.MONTH;
    this.timelineConfig.minView = ComponentView.MONTH;
    this.timelineConfig.maxView = ComponentView.MONTH;
    this.timelineDataHandler = new Subject<TimelineDataSegmentHandler>();

    this.timelineData = {
      dateField: 'payPeriodBeginDate'
    };
    this.timelineDataSet = {
      timelineData: [this.timelineData]
    };
  }

  private initPayExecTimelineData(): Observable<void> {
    this.initPayExecTimelineConfig();
    const execs = [];
    this.timelineLoading = true;

    return this.payResultsService.getPayExecList(this.contractId, this.payExecPagesFetched++)
      .pipe(
        tap(result => {
          execs.push(...result.data);
          this.totalPayExecPagesToFetch = result.total;
        }),
        switchMap(() => this.getOngoingPayPeriod()),
        switchMap(() => this.getPayPeriodsForContract()),
        switchMap(() => this.getPayPeriodContracts()),
        finalize(() => {
          this.timelineLoading = false;
          this.payExecUtils.setPayTypesOnPayExecs(execs, this.payPeriodContracts);
          this.payExecsDated = this.createPayExecList(execs);
          this.setDataRange();
          if (this.payExecsDated.length > 0) {
            this.timelineLandingDate = moment(this.payExecsDated[0].payPeriodBeginDate).toDate();
          }
          this.selectPayExecBySelectedPayType();
        })
      );
  }

  private loadMorePayExecs(first: boolean): Observable<void> {
    if (this.timelineLoading) {
      return EMPTY;
    }

    if (this.payExecPagesFetched === this.totalPayExecPagesToFetch) {
      if (first) {
        this.timelineInitialised = true;
        this.timelineDataHandler.next({
          timelineData: this.timelineData,
          data: this.payExecsDated
        });
      }
      return EMPTY;
    }

    const execs = [];
    this.timelineLoading = true;
    this.cd.detectChanges();
    const lastPayExec = this.payExecsDated[this.payExecsDated.length - 1];
    let pagesToFetch = moment(lastPayExec.payPeriodBeginDate).diff(moment(this.payExecsDateRangeToFetch.shift().from), 'month');

    return this.payResultsService.getPayExecList(this.contractId, this.payExecPagesFetched++)
      .pipe(
        map(result => {
          execs.push(...result.data);
          return [result];
        }),
        expand(res => {
          pagesToFetch -= res.length;
          if (pagesToFetch > 0) {
            return this.getMultiplePayExecPages(pagesToFetch, execs);
          } else {
            return EMPTY;
          }
        }),
        map(() => null),
        finalize(() => {
          if (first) {
            this.timelineInitialised = true;
          }
          this.payExecUtils.setPayTypesOnPayExecs(execs, this.payPeriodContracts);
          const mappedExecs: PayExecWithBeginDate[] = this.createPayExecList(execs);
          this.payExecsDated.push(...mappedExecs);
          this.setDataRange();

          this.timelineDataHandler.next({
            timelineData: this.timelineData,
            data: first ? this.payExecsDated : mappedExecs
          });
          this.timelineLoading = false;

          if (this.payExecsDateRangeToFetch.length) {
            this.loadMorePayExecs(false).subscribe();
          }
        })
      );
  }

  private getMultiplePayExecPages(pagesToFetch: number, payExecs: any[]) {
    let actualPagesToFetch;
    if (this.payExecPagesFetched + this.payExecPagesFetchCount > this.totalPayExecPagesToFetch) {
      actualPagesToFetch = this.totalPayExecPagesToFetch - this.payExecPagesFetched;
    } else if (pagesToFetch > this.payExecPagesFetchCount) {
      actualPagesToFetch = this.payExecPagesFetchCount;
    } else {
      actualPagesToFetch = pagesToFetch;
    }

    return forkJoin(this.fetchPayExecLists(actualPagesToFetch))
      .pipe(
        tap(payExecPages => {
          payExecPages.flatMap(page => page).forEach(exec => payExecs.push(exec));
          this.payExecPagesFetched += payExecPages.length;
        }));
  }

  private fetchPayExecLists(count: number): Observable<PayExecLegacy[]>[] {
    return new Array(count)
      .fill(0)
      .map((v, i) => this.payResultsService.getPayExecList(this.contractId, this.payExecPagesFetched + i)
        .pipe(
          map(result => result.data)));
  }

  private createPayExecList(payExecs: any[]): PayExecWithBeginDate[] {
    const mappedExecs: PayExecWithBeginDate[] = [];
    payExecs.forEach(payExec => {
      const beginDate = this.getPayExecBeginDate(payExec, this.periods);
      mappedExecs.push({
        payExec: payExec,
        payPeriodBeginDate: beginDate
      });
    });
    return mappedExecs;
  }

  private getPayExecBeginDate(payExec, periods: any[]) {
    const payPeriodContract = this.payPeriodContracts.find(ppc => ppc.id === payExec.attributes.payPeriodContract);
    return periods.find(period => period.id === payPeriodContract.payPeriodId).attributes.begindate;
  }

  private setDataRange() {
    if (!this.payExecsDated.length) {
      return;
    }

    let payExecsSorted: PayExecWithBeginDate[] = [...this.payExecsDated];
    payExecsSorted = payExecsSorted.sort((a, b) => moment(a.payPeriodBeginDate).diff(moment(b.payPeriodBeginDate)));
    this.dateRange = {
      from: moment(payExecsSorted[0].payPeriodBeginDate).toDate(),
      to: moment(payExecsSorted[payExecsSorted.length - 1].payPeriodBeginDate).toDate()
    };
  }

  private getRetroValue() {
    this.payResultsService.getRetroValue(this.selectedPayExec.id)
      .subscribe((result: RetroValue[]) => {
        this.retroValue = result;
      });
  }

  private countRetroPayslips() {
    this.payResultsService.countRetroPayslips(this.selectedPayExec.id)
      .subscribe((result: number) => {
        this.retroPayslipsCount = result;
      });
  }

  private setPayPeriodId(payPeriodContractId: number) {
    const selectedPayPeriodContract = this.payPeriodContracts.find(ppc => ppc.id === payPeriodContractId);
    if (selectedPayPeriodContract) {
      this.payPeriodId = selectedPayPeriodContract.payPeriodId;
    }
  }

  public hasPayslip(payExec: any) {
    return payExec ? !!this.getPayslipHtmlFileId(payExec) : false;
  }

  public hasRetroPayslips() {
    return this.retroPayslipsCount && this.retroPayslipsCount > 0;
  }

  public selectTabByIndex(index: number) {
    const activeTab = this.payResultTabset.tabs[index];
    activeTab.active = true;
    this.onTabSelected(activeTab.id);
  }

  public selectTabByName(name: string, source?: string) {
    const activeTab = this.payResultTabset.tabs.find(tab => tab.id === name);
    activeTab.active = true;
    this.onTabSelected(activeTab.id, source);
  }

  public filterPayExecList(payExecs: PayExecWithBeginDate[]) {
    const execs = payExecs || [];
    let payExecsFiltered: PayExecWithBeginDate[];
    if (this.selectedPayType && this.selectedPayType.name) {
      payExecsFiltered = execs.filter((payExec) => {
        return payExec.payExec.attributes.payTypeName === this.selectedPayType.name
          && payExec.payExec.attributes.payPeriodContractPayTypeTransId === this.selectedPayType.transId
          && this.selectedContract && this.selectedPayType.cliRegIds.includes(this.selectedContract.cliRegID);
      });
    } else {
      payExecsFiltered = execs;
    }
    return payExecsFiltered;
  }

  private getOngoingPayPeriod(): Observable<void> {
    return this.employeesService.getContractOngoings(this.contractId)
      .pipe(
        catchError(() => EMPTY),
        map((response: PayPeriodContract[]) => {
          if (response.length > 0) {
            this.payPeriodId = response[0].payPeriodId;
          }
        })
      );
  }

  private selectContract() {
    this.selectedContract = this.contracts.find((c: EmpContractAttributes) => c.id === this.contractId);
  }

  private filterPayTypes(): PayTypeWithCliRegIds[] {
    let payTypes = [...this.payTypes];
    if (this.selectedPayType && this.selectedPayType.name) {
      payTypes = payTypes.filter(payType => this.selectedPayType.name === payType.name && this.selectedPayType.transId === payType.transId);
    }
    return payTypes;
  }

  private selectPayExecBySelectedPayType() {
    const payExecsDatedFiltered: PayExecWithBeginDate[] = this.filterPayExecList(this.payExecsDated);
    if (payExecsDatedFiltered.length) {
      const exec = payExecsDatedFiltered.find(payExec => payExec.payExec.id === this.selectedPayExecId)?.payExec
        || payExecsDatedFiltered[0].payExec;
      this.selectPayExec(exec);
    } else {
      this.selectedPayExec = null;
    }
  }

  public isInRange(date: Date) {
    return this.dateRange && date && moment(date).isBetween(moment(this.dateRange.from), moment(this.dateRange.to), null, '[]');
  }

  public onContractSelected(contract: Contract) {
    this.router.navigate(['payroll/analyze', contract.id]);
  }

  public onTabSelected(tab: string, source?: string) {
    this.showPeriods = tab !== 'Comparison';

    if (tab === 'Payslip') {
      this.router.navigate(['payroll/analyze', this.contractId]);
      setTimeout(() => window.dispatchEvent(RESIZE_EVENT));
      if (source === 'Retro') {
        this.setPayslipIdsForRetro();
      } else {
        this.setPayslipIds();
      }
    }

    this.readyTabs[tab] = true;

    initialTab = tab;
  }

  private setPayslipIds() {
    const htmlFileId: number = this.getPayslipHtmlFileId(this.selectedPayExec);
    const pdfFileId: number = this.getPayslipPdfFileId(this.selectedPayExec);
    if (!htmlFileId || !pdfFileId ||
      (this.payslipHtmlFileId && this.payslipPdfFileId
        && htmlFileId === this.payslipHtmlFileId && pdfFileId === this.payslipPdfFileId)) {
      return;
    }
    this.payslipPdfFileId = pdfFileId;
    this.payslipHtmlFileId = htmlFileId;
  }

  private setPayslipIdsForRetro() {
    const selectedPayExec = this.payExecsDated.find(payExec => payExec.payExec.id === this.selectedPayExecId)?.payExec;
    if (selectedPayExec) {
      this.selectPayExec(selectedPayExec);
    }
    this.setPayslipIds();
  }

  public getPayslipHtmlFileId(payExec: PayExecLegacy): number {
    return payExec ? payExec.attributes.payslipHtmlFileId : undefined;
  }

  public getPayslipPdfFileId(payExec: PayExecLegacy): number {
    return payExec ? payExec.attributes.payslipPdfFileId : undefined;
  }

  public isTabActive(tabName: string) {
    return tabName === initialTab;
  }

  private listenContextPayType() {
    this.employeesContextService.listenContextPayType()
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(payType => {
          return (payType.name !== this.selectedPayType.name || payType.transId !== this.selectedPayType.transId);
        })
      )
      .subscribe((newPayType: PayTypeContextChange) => {
        this.selectedPayType = newPayType;
        if (this.payExecNodeSelection?.open) {
          this.payExecsByPayType = this.groupPayExecsByPayType(this.getPayExecsFromNodeSelectionData(this.payExecNodeSelection));
        }
      });
  }

  public isSelectedExecPayRollTypeNormal(): boolean {
    return this.selectedPayExec.attributes.payRollType === PayExecType.NORMAL;
  }
}
